import { useMemo } from 'react';

import {
  AXIS_AVG_CHAR_WIDTH_PX,
  AXIS_DOMAIN_SAMPLE_COUNT,
  AXIS_PADDING_PX,
  AXIS_TICK_LABEL_GAP_PX,
  AXIS_TICK_LABEL_HEIGHT_PX,
  AXIS_TICK_LENGTH_PX,
  CHART_MARGIN_DEFAULT,
} from '../constants';

import type { DataType } from './formatters';
import { createYAxisFormatterFromTicks, getYAxisFormatter } from './formatters';

export interface ChartMargin {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface AutoMarginOptions {
  /** Whether an XAxis component is present. When true, bottom margin is computed from axis constants. */
  hasXAxis: boolean;
  /**
   * Effective label offset (px) for XAxis — 0 when no label is set, 40 (or the prop value) when
   * a label is present. Used to extend the bottom margin to accommodate the axis label text.
   */
  xAxisLabelOffset: number;
  /** Whether a YAxis component is present. When true, left margin is computed from tick label widths. */
  hasYAxis: boolean;
  /** Explicit Y-axis tick values (available for LineChart). Used for accurate width measurement. */
  yTickValues: number[] | undefined;
  /**
   * Y scale domain [min, max] (always available). Used as a fallback when yTickValues is not set
   * (e.g. ColumnChart) by sampling representative values across the domain.
   */
  yScaleDomain: [number, number] | undefined;
  /** Data type for Y-axis formatting — mirrors YAxis dataType prop */
  yAxisDataType?: DataType;
  /** Custom tick formatter — mirrors YAxis tickFormat prop, takes precedence over yAxisDataType */
  yAxisTickFormat?: (value: number) => string;
  /** Whether a right YAxis component is present. When true, right margin is computed from tick label widths. */
  hasYAxisRight?: boolean;
  yTickValuesRight?: number[];
  yScaleRightDomain?: [number, number];
  yAxisDataTypeRight?: DataType;
  yAxisTickFormatRight?: (value: number) => string;
  /**
   * Effective right-axis title offset (px). 55 when a right YAxis label is present and
   * labelOffset is omitted. Added to the tick-based right margin so the rotated title
   * is not clipped at the SVG edge.
   */
  yAxisLabelOffsetRight?: number;
  /** When false, skips auto-computation and uses manualMargin merged with defaults */
  enabled: boolean;
  manualMargin?: Partial<ChartMargin>;
}

/**
 * Generate representative numeric values across a domain for width estimation.
 * Used when explicit yTickValues are unavailable (e.g. ColumnChart).
 */
function sampleDomain(domain: [number, number], count: number): number[] {
  const [min, max] = domain;
  if (min === max) {
    return [min];
  }
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, i) => min + i * step);
}

/**
 * Resolves the Y-axis tick formatter using the same priority logic as the YAxis component:
 * 1. Explicit tickFormat prop
 * 2. Domain-aware formatter when tick values + dataType are both available
 * 3. Generic dataType formatter
 * 4. Identity (raw number) when neither is available
 *
 * IMPORTANT: pass the original `yTickValues` here, not domain-sampled values.
 * The formatter choice must mirror what YAxis does: domain-aware only when yTickValues
 * are explicitly available. Passing sampled values would incorrectly enter the domain-aware
 * path for charts like ColumnChart that never expose yTickValues.
 */
function resolveYAxisFormatter(
  tickValues: number[],
  dataType?: DataType,
  tickFormat?: (value: number) => string,
): (value: number) => string {
  if (tickFormat) {
    return tickFormat;
  }
  if (tickValues.length > 0 && dataType) {
    return createYAxisFormatterFromTicks(tickValues, dataType);
  }
  return getYAxisFormatter(dataType) ?? String;
}

/**
 * Finds the longest formatted Y-axis tick label by character count.
 */
function computeMaxTickLabelLength(tickValues: number[], formatter: (v: number) => string): number {
  return tickValues.reduce((max, value) => Math.max(max, formatter(value).length), 0);
}

/**
 * Computes the required bottom margin for an XAxis.
 *
 * Without a label the bottom must fit: tick mark + gap + tick label + padding.
 * With a label the bottom margin must accommodate visx's label position:
 * `y = tickLength + labelOffset + tickLabelFontSize + labelFontSize` (from getLabelTransform).
 */
function computeXAxisBottomMargin(xAxisLabelOffset: number): number {
  // Space needed for tick mark + tick label text.
  // Note: AXIS_TICK_LABEL_GAP_PX is intentionally excluded here — it applies to the Y-axis
  // left margin where the gap is explicit. Visx positions X-axis labels via `dy: 0.25em`
  // (already baked into AXIS_TICK_LABEL_HEIGHT_PX), not a separate gap constant.
  const base = AXIS_TICK_LENGTH_PX + AXIS_TICK_LABEL_HEIGHT_PX;

  if (xAxisLabelOffset === 0) {
    return base;
  }

  // Visx positions the axis label at y = tickLength + labelOffset + tickLabelFontSize + labelFontSize
  // (from @visx/axis getLabelTransform). Our tick labels use CSS var() font sizes, which are
  // non-numeric, so visx's Math.max(10, ...) fallback gives tickLabelFontSize = 10. The axis
  // label font size is 14 (svgLabelBig.fontSize in chartTheme.ts). The bottom margin must be at
  // least equal to that total y offset for the label baseline to land within the SVG viewport.
  const VISX_TICK_LABEL_MIN_FONT_SIZE_PX = 10; // visx Math.max(10, ...) fallback for non-numeric fontSizes
  const AXIS_LABEL_FONT_SIZE_PX = 14; // svgLabelBig.fontSize in chartTheme.ts
  // Add AXIS_TICK_LENGTH_PX (4px) as bottom padding so the label baseline doesn't sit exactly
  // at the SVG viewport edge, which would risk antialiasing/pixel-snap clipping.
  const withLabel =
    AXIS_TICK_LENGTH_PX +
    xAxisLabelOffset +
    VISX_TICK_LABEL_MIN_FONT_SIZE_PX +
    AXIS_LABEL_FONT_SIZE_PX +
    AXIS_TICK_LENGTH_PX;
  return Math.max(base, withLabel);
}

function computeYAxisSideMargin(
  hasAxis: boolean,
  tickValues: number[] | undefined,
  scaleDomain: [number, number] | undefined,
  dataType: DataType | undefined,
  tickFormat: ((value: number) => string) | undefined,
  manualOverride: number | undefined,
  labelOffset: number = 0,
): number {
  if (manualOverride !== undefined) {
    return manualOverride;
  }
  if (!hasAxis) {
    return 0;
  }
  const valuesToMeasure =
    tickValues && tickValues.length > 0
      ? tickValues
      : scaleDomain
        ? sampleDomain(scaleDomain, AXIS_DOMAIN_SAMPLE_COUNT)
        : [];
  if (valuesToMeasure.length === 0) {
    return labelOffset;
  }
  const formatter = resolveYAxisFormatter(tickValues ?? [], dataType, tickFormat);
  const maxLabelLength = computeMaxTickLabelLength(valuesToMeasure, formatter);
  return (
    Math.ceil(maxLabelLength * AXIS_AVG_CHAR_WIDTH_PX) +
    AXIS_TICK_LENGTH_PX +
    AXIS_TICK_LABEL_GAP_PX +
    AXIS_PADDING_PX +
    labelOffset
  );
}

/**
 * Hook that computes chart margins automatically based on which axis components are present.
 *
 * When `enabled` is true:
 * - bottom: computed from XAxis presence and label offset (zero when no XAxis).
 * - left: computed from the widest formatted Y-axis tick label (zero when no YAxis).
 * - right: computed from the widest formatted right Y-axis tick label plus title offset
 *   when a right YAxis is present, otherwise CHART_MARGIN_DEFAULT.right.
 * - top: CHART_MARGIN_DEFAULT.top.
 *
 * `manualMargin` top/bottom/left always override the auto-computed value. When a right
 * YAxis is present, `manualMargin.right` is a floor so a small x-axis pad cannot clip
 * right-axis tick labels.
 */
export function useAutoMargin({
  hasXAxis,
  xAxisLabelOffset,
  hasYAxis,
  yTickValues,
  yScaleDomain,
  yAxisDataType,
  yAxisTickFormat,
  hasYAxisRight = false,
  yTickValuesRight,
  yScaleRightDomain,
  yAxisDataTypeRight,
  yAxisTickFormatRight,
  yAxisLabelOffsetRight = 0,
  enabled,
  manualMargin,
}: AutoMarginOptions): ChartMargin {
  return useMemo(() => {
    const top = manualMargin?.top ?? CHART_MARGIN_DEFAULT.top;

    if (!enabled) {
      return {
        top,
        right: manualMargin?.right ?? CHART_MARGIN_DEFAULT.right,
        bottom: manualMargin?.bottom ?? CHART_MARGIN_DEFAULT.bottom,
        left: manualMargin?.left ?? CHART_MARGIN_DEFAULT.left,
      };
    }

    const bottom =
      manualMargin?.bottom !== undefined
        ? manualMargin.bottom
        : hasXAxis
          ? computeXAxisBottomMargin(xAxisLabelOffset)
          : 0;

    const left = computeYAxisSideMargin(
      hasYAxis,
      yTickValues,
      yScaleDomain,
      yAxisDataType,
      yAxisTickFormat,
      manualMargin?.left,
    );

    const computedRight = hasYAxisRight
      ? computeYAxisSideMargin(
          true,
          yTickValuesRight,
          yScaleRightDomain,
          yAxisDataTypeRight,
          yAxisTickFormatRight,
          undefined,
          yAxisLabelOffsetRight,
        )
      : (manualMargin?.right ?? CHART_MARGIN_DEFAULT.right);
    const right =
      hasYAxisRight && manualMargin?.right !== undefined ? Math.max(computedRight, manualMargin.right) : computedRight;

    return { top, right, bottom, left };
  }, [
    hasXAxis,
    xAxisLabelOffset,
    hasYAxis,
    yTickValues,
    yScaleDomain,
    yAxisDataType,
    yAxisTickFormat,
    hasYAxisRight,
    yTickValuesRight,
    yScaleRightDomain,
    yAxisDataTypeRight,
    yAxisTickFormatRight,
    yAxisLabelOffsetRight,
    enabled,
    manualMargin,
  ]);
}
